import { useEffect } from "react";
import "./About.css";

const About = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      [data-aos] {
        opacity: 0;
        transition-property: opacity, transform;
        transition-duration: 0.8s;
      }
      [data-aos].aos-animate {
        opacity: 1;
      }
      [data-aos="fade-up"] {
        transform: translateY(50px);
      }
      [data-aos="fade-up"].aos-animate {
        transform: translateY(0);
      }
      [data-aos="fade-right"] {
        transform: translateX(-50px);
      }
      [data-aos="fade-right"].aos-animate {
        transform: translateX(0);
      }
      [data-aos="fade-left"] {
        transform: translateX(50px);
      }
      [data-aos="fade-left"].aos-animate {
        transform: translateX(0);
      }
      [data-aos="zoom-in"] {
        transform: scale(0.9);
      }
      [data-aos="zoom-in"].aos-animate {
        transform: scale(1);
      }
    `;
    document.head.appendChild(style);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("aos-animate");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll("[data-aos]").forEach((el) => {
      observer.observe(el);
    });

    const counters = document.querySelectorAll(".stat-number");
    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            const finalValue = parseInt(
              target.getAttribute("data-count") || "0"
            );
            let current = 0;
            const increment = finalValue / 60;

            const updateCounter = () => {
              current += increment;
              if (current < finalValue) {
                target.textContent = Math.floor(current).toString();
                requestAnimationFrame(updateCounter);
              } else {
                target.textContent = finalValue + "+";
              }
            };

            updateCounter();
            statsObserver.unobserve(target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((counter) => statsObserver.observe(counter));
  }, []);

  const values = [
    {
      icon: "💡",
      title: "Innovation",
      description:
        "We continuously challenge conventional boundaries and explore new possibilities to create groundbreaking solutions that transform industries and empower communities.",
    },
    {
      icon: "🤝",
      title: "Integrity",
      description:
        "We operate with complete transparency, honesty, and ethical practices, building trust through accountability and maintaining the highest professional standards.",
    },
    {
      icon: "🎯",
      title: "Excellence",
      description:
        "We are committed to delivering exceptional quality in every service and solution, consistently exceeding expectations and setting new industry benchmarks.",
    },
  ];

  const team = [
    {
      role: "Leadership Team",
      icon: "👔",
      description: "Strategic vision and direction",
    },
    {
      role: "Technical Experts",
      icon: "💻",
      description: "Cutting-edge technology solutions",
    },
    {
      role: "Business Consultants",
      icon: "📊",
      description: "Growth and strategy advisory",
    },
    {
      role: "Solar Engineers",
      icon: "☀️",
      description: "Renewable energy specialists",
    },
    {
      role: "Real Estate Advisors",
      icon: "🏡",
      description: "Property investment experts",
    },
    {
      role: "Training Specialists",
      icon: "🎓",
      description: "Professional development leaders",
    },
  ];

  const services = [
    {
      icon: "🎓",
      title: "Business and Tech Training",
      description:
        "Empowering individuals and teams with essential business skills, entrepreneurship knowledge, and cutting-edge technical expertise.",
      features: [
        "Business & Leadership Skills",
        "Technical & IT Training",
        "Entrepreneurship Programs",
        "Digital Marketing",
        "Professional Development",
      ],
    },
    {
      icon: "💡",
      title: "Entrepreneurship Development",
      description:
        "Supporting aspiring and existing entrepreneurs with business advisory, startup guidance, and growth strategies.",
      features: [
        "Business Advisory",
        "Startup Guidance",
        "Growth Strategies",
        "Mentorship Programs",
        "Funding Support",
      ],
    },
    {
      icon: "☀️",
      title: "Sustainable Energy Solutions",
      description:
        "Delivering comprehensive solar power installations and renewable energy systems that reduce costs and promote sustainability.",
      features: [
        "Solar System Design",
        "Professional Installation",
        "Maintenance & Support",
        "Energy Consultation",
        "Cost-Benefit Analysis",
      ],
    },
    {
      icon: "🏗️",
      title: "Real Estate Services",
      description:
        "Providing strategic property services, investment opportunities, and construction solutions to build lasting wealth.",
      features: [
        "Property Sales & Acquisition",
        "Investment Advisory",
        "Construction Services",
        "Development Consulting",
        "Property Management",
      ],
    },
  ];

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="hero-particles"></div>
        <div className="container">
          <div className="about-hero-content">
            <span className="section-badge" data-aos="fade-down">
              About LICO
            </span>
            <h1 data-aos="fade-up" data-aos-delay="100">
              Learn and Innovate{" "}
              <span className="gradient-text">Technologies Limited</span>
            </h1>
            <p data-aos="fade-up" data-aos-delay="200">
              Empowering individuals and businesses through innovative
              solutions, professional training, sustainable energy, custom
              software, and strategic real estate services since our
              establishment.
            </p>
          </div>
        </div>
      </section>

      <section className="about-stats">
        <div className="container">
          <div className="stats-grid" data-aos="fade-up">
            <div className="stat-box">
              <div className="stat-number" data-count="500">
                0
              </div>
              <div className="stat-label">Trained Professionals</div>
            </div>
            <div className="stat-box">
              <div className="stat-number" data-count="150">
                0
              </div>
              <div className="stat-label">Solar Installations</div>
            </div>
            <div className="stat-box">
              <div className="stat-number" data-count="50">
                0
              </div>
              <div className="stat-label">Software Solutions</div>
            </div>
            <div className="stat-box">
              <div className="stat-number" data-count="100">
                0
              </div>
              <div className="stat-label">Real Estate Deals</div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-story">
        <div className="container">
          <div className="story-content">
            <div className="story-text" data-aos="fade-right">
              <h2>Our Story</h2>
              <p>
                <strong>LICO (Learn and Innovate Technologies Limited)</strong>{" "}
                was founded with a clear vision: to bridge the gap between
                traditional services and modern technological solutions. Based
                in New Heaven, Enugu State, Nigeria, we have grown from a small
                startup to a leading provider of diverse, high-quality services.
              </p>
              <p>
                Our journey began with a simple belief that everyone deserves
                access to quality training, sustainable energy solutions,
                cutting-edge software, and reliable real estate services. Today,
                we proudly serve hundreds of satisfied clients across Nigeria,
                helping them achieve their goals and transform their futures.
              </p>
              <p>
                What sets us apart is our commitment to excellence, innovation,
                and integrity in everything we do. We don't just provide
                services; we build lasting relationships and create value that
                endures. Our integrated approach combines education, technology,
                sustainability, and smart investments to deliver comprehensive
                solutions.
              </p>
            </div>
            <div className="story-image" data-aos="fade-left">
              <div className="image-placeholder">
                <span className="placeholder-icon">🚀</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mission-vision">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card" data-aos="fade-up" data-aos-delay="100">
              <div className="mv-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>
                To empower individuals and organizations with innovative
                solutions, quality training, and exceptional services that drive
                growth, sustainability, and success in an ever-evolving world.
                We strive to make professional development, renewable energy,
                custom software, and property investments accessible to all.
              </p>
            </div>
            <div className="mv-card" data-aos="fade-up" data-aos-delay="200">
              <div className="mv-icon">🔭</div>
              <h3>Our Vision</h3>
              <p>
                To be the leading provider of integrated technology, training,
                energy, and real estate solutions in Nigeria and beyond,
                recognized for excellence, innovation, and positive impact on
                communities. We envision a future where businesses and
                individuals thrive through sustainable practices and continuous
                learning.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-services">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">What We Offer</span>
            <h2>Comprehensive Solutions</h2>
            <p>
              Four core service areas designed to transform how you work, power
              your operations, leverage technology, and invest in property
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <div className="service-features">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="service-feature">
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-values">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">Our Core Values</span>
            <h2>What Drives Us</h2>
            <p>The principles that guide everything we do at LICO</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div
                key={index}
                className="value-card"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-team">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">Our Team</span>
            <h2>Expert Professionals</h2>
            <p>A diverse team of dedicated experts committed to your success</p>
          </div>
          <div className="team-grid">
            {team.map((member, index) => (
              <div
                key={index}
                className="team-card"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="team-icon">{member.icon}</div>
                <h3>{member.role}</h3>
                <p>{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="why-choose">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">Why Choose LICO</span>
            <h2>What Makes Us Different</h2>
            <p>
              LICO isn't just a service provider—we're your partner in growth,
              innovation, and sustainability
            </p>
          </div>
          <div className="choose-grid">
            <div
              className="choose-card"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <h3>🎯 Four Pillars, One Vision</h3>
              <p>
                We uniquely combine training, solar energy, software
                development, and real estate services under one roof, providing
                integrated solutions that address multiple aspects of your
                growth and wealth-building goals.
              </p>
            </div>
            <div
              className="choose-card"
              data-aos="fade-up"
              data-aos-delay="150"
            >
              <h3>👨‍💼 Industry Practitioners</h3>
              <p>
                Our trainers, developers, and real estate professionals aren't
                just educators—they're active practitioners with real-world
                experience, bringing current industry practices and practical
                insights to every engagement.
              </p>
            </div>
            <div
              className="choose-card"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <h3>🌍 Local Expertise, Global Standards</h3>
              <p>
                We understand the Nigerian business and property landscape while
                delivering solutions that meet international quality standards,
                giving you the best of both worlds.
              </p>
            </div>
            <div
              className="choose-card"
              data-aos="fade-up"
              data-aos-delay="250"
            >
              <h3>📈 Results-Driven Approach</h3>
              <p>
                Every training program, solar installation, software solution,
                and property transaction is designed with measurable outcomes in
                mind—your success is our benchmark.
              </p>
            </div>
            <div
              className="choose-card"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <h3>🔄 Continuous Support</h3>
              <p>
                Our relationship doesn't end at delivery. We provide ongoing
                support, maintenance, and advisory services to ensure your
                continued success and adaptation to changing needs.
              </p>
            </div>
            <div
              className="choose-card"
              data-aos="fade-up"
              data-aos-delay="350"
            >
              <h3>💰 Value-Focused Pricing</h3>
              <p>
                We offer competitive, transparent pricing with flexible payment
                options, making professional training, renewable energy, custom
                software, and property investments accessible to diverse
                clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-commitment">
        <div className="container">
          <div className="commitment-content" data-aos="fade-up">
            <div className="commitment-text">
              <h2>Our Commitment to You</h2>
              <p>
                At LICO, we understand that true transformation happens when
                education, technology, sustainability, and smart investments
                converge. Whether you're an aspiring entrepreneur, a business
                looking to go green, an organization needing custom software, or
                an investor seeking property opportunities, we provide the
                expertise, support, and innovation to help you succeed.
              </p>
              <div className="commitment-points">
                <div className="commitment-point">
                  <span className="point-icon">✓</span>
                  <div>
                    <h4>Quality Assurance</h4>
                    <p>Every project undergoes rigorous quality checks</p>
                  </div>
                </div>
                <div className="commitment-point">
                  <span className="point-icon">✓</span>
                  <div>
                    <h4>Timely Delivery</h4>
                    <p>We respect your time and meet deadlines consistently</p>
                  </div>
                </div>
                <div className="commitment-point">
                  <span className="point-icon">✓</span>
                  <div>
                    <h4>Customer Satisfaction</h4>
                    <p>Your success and satisfaction are our top priorities</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-cta" data-aos="zoom-in">
        <div className="container">
          <h2>Ready to Work With Us?</h2>
          <p>
            Join our growing family of satisfied clients and experience the LICO
            difference. Let's transform your future together.
          </p>
          <a href="#contact" className="btn btn-light">
            Get In Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
